import React, { useEffect, useState } from "react";
import axios from "axios";
import styled from "styled-components";
import { FiExternalLink, FiRefreshCw, FiSearch } from "react-icons/fi";
import API_URL from "../../utils";

const PageShell = styled.section`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
`;

const SearchBox = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 260px;
  padding: 0.65rem 0.85rem;
  border: 1px solid ${(props) => (props.isDarkMode ? "#4a5568" : "#e2e8f0")};
  border-radius: 8px;
  background: ${(props) => (props.isDarkMode ? "#2d3748" : "#fff")};
  color: ${(props) => (props.isDarkMode ? "#e2e8f0" : "#2d3748")};

  input {
    width: 100%;
    border: 0;
    outline: 0;
    color: inherit;
    background: transparent;
  }
`;

const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  min-height: 38px;
  padding: 0.55rem 0.8rem;
  border: 0;
  border-radius: 8px;
  color: #fff;
  background: ${(props) => (props.secondary ? "#4a5568" : "#667eea")};
  cursor: pointer;
  font-weight: 600;

  &:disabled {
    background: #a0aec0;
    cursor: not-allowed;
  }
`;

const TableWrap = styled.div`
  overflow: auto;
  border: 1px solid ${(props) => (props.isDarkMode ? "#4a5568" : "#e2e8f0")};
  border-radius: 8px;
  background: ${(props) => (props.isDarkMode ? "#2d3748" : "#fff")};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 860px;

  th,
  td {
    padding: 0.8rem;
    border-bottom: 1px solid
      ${(props) => (props.isDarkMode ? "#4a5568" : "#edf2f7")};
    text-align: left;
    vertical-align: middle;
  }

  th {
    color: ${(props) => (props.isDarkMode ? "#e2e8f0" : "#2d3748")};
    background: ${(props) => (props.isDarkMode ? "#1a202c" : "#f8fafc")};
    font-size: 0.86rem;
  }

  td {
    color: ${(props) => (props.isDarkMode ? "#cbd5e0" : "#4a5568")};
    font-size: 0.92rem;
  }
`;

const Notice = styled.div`
  padding: 0.85rem 1rem;
  border-radius: 8px;
  color: ${(props) => (props.error ? "#9b2c2c" : "#2d3748")};
  background: ${(props) => (props.error ? "#fed7d7" : "#edf2f7")};
`;

const PaginationBar = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.7rem;
  color: ${(props) => (props.isDarkMode ? "#cbd5e0" : "#4a5568")};
`;

const formatDate = (value) => {
  if (!value) return "";
  return new Date(value).toLocaleString();
};

function RealflowCsvPage({ isDarkMode }) {
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 25,
    total: 0,
    totalPages: 0,
  });
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [placingId, setPlacingId] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("token")}`,
  });

  const fetchCsvs = async (page = 1, searchTerm = search) => {
    setLoading(true);
    setError("");
    try {
      const response = await axios.get(`${API_URL}/realflow/csvs`, {
        headers: authHeaders(),
        params: {
          page,
          pageSize: pagination.pageSize,
          search: searchTerm,
        },
      });
      setItems(response.data.items || []);
      setPagination(response.data.pagination || pagination);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load RealFlow CSVs.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCsvs(1, "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    fetchCsvs(1, search);
  };

  const handlePlaceOrder = async (id) => {
    setPlacingId(id);
    setError("");
    setMessage("");
    try {
      const response = await axios.post(
        `${API_URL}/realflow/csvs/${id}/place-order`,
        {},
        { headers: authHeaders() }
      );
      const { shopUrl, candidateUploadId, recordCount } = response.data;
      setMessage(
        `Prepared ${recordCount} records as ${candidateUploadId}. Opening shop page.`
      );
      window.open(shopUrl, "_blank", "noopener,noreferrer");
    } catch (err) {
      setError(
        err.response?.data?.message || "Failed to prepare RealFlow place order."
      );
    } finally {
      setPlacingId("");
    }
  };

  return (
    <PageShell>
      <Toolbar>
        <form onSubmit={handleSearchSubmit}>
          <SearchBox isDarkMode={isDarkMode}>
            <FiSearch />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search account, filename, or path"
            />
          </SearchBox>
        </form>
        <ActionButton
          type="button"
          secondary
          onClick={() => fetchCsvs(pagination.page, search)}
          disabled={loading}
        >
          <FiRefreshCw />
          Refresh
        </ActionButton>
      </Toolbar>

      {message && <Notice>{message}</Notice>}
      {error && <Notice error>{error}</Notice>}

      <TableWrap isDarkMode={isDarkMode}>
        <Table isDarkMode={isDarkMode}>
          <thead>
            <tr>
              <th>Received</th>
              <th>YLHQ ID</th>
              <th>HQ Data Hub ID</th>
              <th>File</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="6">Loading RealFlow CSVs...</td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan="6">No RealFlow CSVs found.</td>
              </tr>
            ) : (
              items.map((item) => (
                <tr key={item.id}>
                  <td>{formatDate(item.createdAt)}</td>
                  <td>{item.ylhqId || ""}</td>
                  <td>{item.hqdatahubId || ""}</td>
                  <td>{item.originalFileName || item.csvPath}</td>
                  <td>{item.status}</td>
                  <td>
                    <ActionButton
                      type="button"
                      onClick={() => handlePlaceOrder(item.id)}
                      disabled={placingId === item.id}
                    >
                      <FiExternalLink />
                      {placingId === item.id ? "Preparing..." : "Place Order"}
                    </ActionButton>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </TableWrap>

      <PaginationBar isDarkMode={isDarkMode}>
        <ActionButton
          type="button"
          secondary
          disabled={pagination.page <= 1 || loading}
          onClick={() => fetchCsvs(pagination.page - 1, search)}
        >
          Previous
        </ActionButton>
        <span>
          Page {pagination.page} of {Math.max(pagination.totalPages || 1, 1)}
        </span>
        <ActionButton
          type="button"
          secondary
          disabled={pagination.page >= pagination.totalPages || loading}
          onClick={() => fetchCsvs(pagination.page + 1, search)}
        >
          Next
        </ActionButton>
      </PaginationBar>
    </PageShell>
  );
}

export default RealflowCsvPage;
